
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

interface ViewJobDescriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleId: string;
}

interface RoleDescriptionData {
  text?: string;
  pdf_url?: string;
  pdf_filename?: string;
}

export function ViewJobDescriptionDialog({
  open,
  onOpenChange,
  roleId
}: ViewJobDescriptionDialogProps) {
  const [roleData, setRoleData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [jobDescriptionText, setJobDescriptionText] = useState<string>("");

  useEffect(() => {
    if (open && roleId) {
      fetchRoleData();
    }
  }, [open, roleId]);

  const fetchRoleData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('role_jd')
        .select('role_title, role_description')
        .eq('id', roleId)
        .single();

      if (error) throw error;
      setRoleData(data);

      // Parse the role_description JSON properly
      let roleDescription: RoleDescriptionData = {};
      
      if (data?.role_description) {
        if (typeof data.role_description === 'string') {
          try {
            roleDescription = JSON.parse(data.role_description);
          } catch {
            roleDescription = { text: data.role_description };
          }
        } else if (typeof data.role_description === 'object' && data.role_description !== null) {
          roleDescription = data.role_description as RoleDescriptionData;
        }
      }

      // If there's a PDF URL, fetch the content from that URL
      if (roleDescription.pdf_url) {
        try {
          const response = await fetch(roleDescription.pdf_url);
          if (response.ok) {
            const textContent = await response.text();
            setJobDescriptionText(textContent);
          } else {
            console.error('Failed to fetch PDF content');
            setJobDescriptionText(roleDescription.text || 'Failed to load job description content');
          }
        } catch (fetchError) {
          console.error('Error fetching PDF content:', fetchError);
          setJobDescriptionText(roleDescription.text || 'Failed to load job description content');
        }
      } else {
        // Use the text content directly
        setJobDescriptionText(roleDescription.text || '');
      }
    } catch (error) {
      console.error('Error fetching role data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatJobDescription = (description: string) => {
    if (!description) return '';
    // Replace €¢ with •
    return description.replace(/€¢/g, '•');
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] bg-secondary border-primary/20">
          <div className="flex items-center justify-center p-8">
            <div className="text-white">Loading...</div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!roleData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] bg-secondary border-primary/20">
          <div className="flex items-center justify-center p-8">
            <div className="text-white">No job description found</div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const formattedDescription = formatJobDescription(jobDescriptionText);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] bg-secondary border-primary/20">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-primary">
            View Job Description
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-2">
          {/* Job Description Content */}
          <div className="space-y-2">
            <ScrollArea className="h-[400px] w-full rounded-md border border-primary/20 bg-secondary/30 p-4">
              <div className="text-white text-[11px] whitespace-pre-wrap">
                {formattedDescription}
              </div>
            </ScrollArea>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-4">
            <Button 
              onClick={() => onOpenChange(false)}
              className="bg-primary hover:bg-primary/90 text-black px-8"
            >
              OK
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
